import messages from "../messages/messages.js";
import API_URL, {withSite} from "../includes/api.js";
import {useEffect, useRef, useState} from "preact/hooks";
import {FlagIcon, SpeakerWaveIcon, SpeakerXMarkIcon} from "@heroicons/react/24/solid";
import {reportSong} from "../includes/requests.js";
import ytReady from "../includes/youtube.js";
import {Song} from "./Song.jsx";

export function RadioComponent({subscribers_id = null, compactUpcoming = false}) {
    const mainUrl = "https://www." + messages["DOMAIN"];
    const playerRef = useRef(null);      // YouTube Player instance
    const containerRef = useRef(null);   // DOM node for the iframe
    // Offset (seconds) = serverTime - localTime.
    const serverOffsetRef = useRef(null);

    const [queue, setQueue] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [volume, setVolume] = useState(0);
    const preMuteVolumeRef = useRef(0.5);
    const [tick, setTick] = useState(0);

    const isMuted = volume === 0;

    // Refs mirroring state so async callbacks always read the latest value.
    const currentIndexRef = useRef(0);
    const queueRef = useRef([]);
    const volumeRef = useRef(0);
    useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
    useEffect(() => { queueRef.current = queue; }, [queue]);
    useEffect(() => { volumeRef.current = volume; }, [volume]);

    // ── YouTube Player lifecycle ──────────────────────────────────
    useEffect(() => {
        let player = null;

        ytReady.then((YT) => {
            if (!containerRef.current) return;
            player = new YT.Player(containerRef.current, {
                height: '1',
                width: '1',
                playerVars: {
                    autoplay: 0,
                    playsinline: 1,
                    controls: 0,
                    disablekb: 1,
                    fs: 0,
                    modestbranding: 1,
                    origin: window.location.origin,
                },
                events: {
                    onReady: () => {
                        playerRef.current = player;
                        const song = queueRef.current[currentIndexRef.current];
                        if (song) loadSong(player, song);
                    },
                    onStateChange: (e) => {
                        if (e.data === 0) handleEnded(); // ENDED
                    },
                    onError: (e) => {
                        console.error('[Radio] YouTube player error:', e.data);
                    },
                },
            });
        });

        return () => {
            if (player) player.destroy();
            playerRef.current = null;
        };
    }, []);

    // ── Volume sync ───────────────────────────────────────────────
    useEffect(() => {
        const p = playerRef.current;
        if (!p) return;
        if (isMuted) {
            p.mute();
        } else {
            p.unMute();
            p.setVolume(Math.round(volume * 100));
        }
    }, [volume, isMuted]);

    // ── Song changes ──────────────────────────────────────────────
    const currentSong = queue[currentIndex] || null;
    const s = currentSong?.youtube_url || null;
    const prevVideoRef = useRef(null);

    useEffect(() => {
        if (!s || s === prevVideoRef.current) return;
        prevVideoRef.current = s;
        const p = playerRef.current;
        if (p) loadSong(p, currentSong);
    }, [s]);

    const loadSong = (player, song) => {
        if (!song) return;
        const seek = getSeekForSong(song);
        player.loadVideoById({videoId: song.youtube_url, startSeconds: seek});
        if (volumeRef.current === 0) {
            player.mute();
        } else {
            player.unMute();
            player.setVolume(Math.round(volumeRef.current * 100));
        }
    };

    // ── Tick for progress bar ─────────────────────────────────────
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const formatTime = (s) => {
        if (!Number.isFinite(s) || s < 0) s = 0;
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, "0")}`;
    };

    // ── Queue fetching ────────────────────────────────────────────
    useEffect(() => {
        fetchQueue();
        const interval = setInterval(fetchQueue, 45000);
        return () => clearInterval(interval);
    }, [subscribers_id]);

    const getServerNow = () => {
        const localNow = Date.now() / 1000;
        if (serverOffsetRef.current !== null) {
            return localNow + serverOffsetRef.current;
        }
        return localNow;
    };

    const findCurrentIndex = (q, serverNow) => {
        for (let i = 0; i < q.length; i++) {
            const song = q[i];
            if (song.start_time <= serverNow && serverNow < song.start_time + song.video_length) {
                return i;
            }
        }
        for (let i = 0; i < q.length; i++) {
            if (q[i].start_time > serverNow) return i;
        }
        return 0;
    };

    const fetchQueue = async () => {
        let url = API_URL + '/radio/get';
        if (subscribers_id) {
            url += '?subscribers_id=' + subscribers_id;
        }
        const response = await fetch(withSite(url));
        const data = await response.json();
        const newQueue = data?.data?.queue;
        const serverTime = data?.data?.server_time;
        if (!newQueue || !newQueue.length || !serverTime) return;

        serverOffsetRef.current = serverTime - (Date.now() / 1000);

        const liveIndex = currentIndexRef.current;
        const prevQueue = queueRef.current;
        const prevCurrent = prevQueue[liveIndex];

        let newIndex = findCurrentIndex(newQueue, serverTime);

        if (prevCurrent) {
            const matchIdx = newQueue.findIndex(
                (sng) => sng.youtube_url === prevCurrent.youtube_url &&
                    sng.start_time === prevCurrent.start_time
            );
            if (matchIdx !== -1) newIndex = matchIdx;
        }

        setQueue(newQueue);
        setCurrentIndex(newIndex);
    };

    const getSeekForSong = (song) => {
        if (!song) return 0;
        const serverNow = getServerNow();
        const offset = serverNow - song.start_time;
        if (offset < 0) return 0;
        if (song.video_length && offset > song.video_length) return 0;
        return offset;
    };

    const handleEnded = () => {
        const liveIndex = currentIndexRef.current;
        const liveQueue = queueRef.current;
        const serverNow = getServerNow();
        const ended = liveQueue[liveIndex];

        const effectiveNow = ended
            ? Math.max(serverNow, ended.start_time + ended.video_length + 0.001)
            : serverNow;
        const scheduledIdx = findCurrentIndex(liveQueue, effectiveNow);
        const scheduledSong = liveQueue[scheduledIdx];

        if (scheduledSong && scheduledSong.start_time <= effectiveNow) {
            setCurrentIndex(scheduledIdx);
        } else {
            fetchQueue();
        }
    };

    // ── Tune in / out ─────────────────────────────────────────────
    const handleTuneIn = () => {
        setVolume(preMuteVolumeRef.current || 0.5);
    };

    const handleTuneOut = () => {
        preMuteVolumeRef.current = volume || 0.5;
        setVolume(0);
    };

    // ── Derived values for UI ─────────────────────────────────────
    const title = currentSong?.title || null;
    const authorName = currentSong?.author_name || null;
    const categoryName = currentSong?.category_name || null;
    const categoryId = currentSong?.category_id || null;

    const upcoming = queue.slice(currentIndex + 1, currentIndex + 5);

    void tick;
    const totalLength = currentSong?.video_length || 0;
    let elapsed = 0;
    if (currentSong) {
        const playerTime = playerRef.current?.getCurrentTime?.();
        if (Number.isFinite(playerTime) && playerTime > 0) {
            elapsed = playerTime;
        } else {
            elapsed = Math.max(0, Math.min(totalLength, getServerNow() - currentSong.start_time));
        }
    }
    const remaining = Math.max(0, totalLength - elapsed);
    const progressPct = totalLength > 0 ? (elapsed / totalLength) * 100 : 0;

    return (
        <>
            {s && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 rounded-2xl bg-gradient-to-br from-gray-900 via-gray-900 to-black border border-white/10 shadow-2xl overflow-hidden">
                        <div className="relative">
                            <img src={`https://i1.ytimg.com/vi/${s}/sddefault.jpg`} alt="song thumbnail"
                                 className="w-full aspect-video object-cover"/>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20"/>
                            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full pl-2 pr-3 py-1">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"/>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"/>
                                </span>
                                <span className="text-[10px] uppercase tracking-widest font-semibold text-white">On Air</span>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-5">
                                <a href={mainUrl + "/playlist?p=" + categoryId} target="_blank" rel="noopener noreferrer"
                                   className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-white bg-blue-600/90 hover:bg-blue-600 transition px-2 py-0.5 rounded-sm mb-2">
                                    {categoryName}
                                </a>
                                <a href={mainUrl + '/song?s=' + s + '&p=' + categoryId} target="_blank" rel="noopener noreferrer"
                                   className="block text-white text-xl font-bold leading-tight hover:text-red-300 transition line-clamp-2">
                                    {title}
                                </a>
                                <a href={mainUrl + '/search?q=' + encodeURIComponent(authorName)} target="_blank" rel="noopener noreferrer"
                                   className="text-sm text-white/70 hover:text-white transition">
                                    {authorName}
                                </a>
                            </div>
                        </div>

                        <div className="p-4">
                            <div className="mb-3">
                                <div
                                    role="progressbar"
                                    aria-label="Song progress"
                                    aria-valuenow={Math.round(progressPct)}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                                    <div className="h-full bg-red-500 transition-[width] duration-1000 ease-linear"
                                         style={{width: `${progressPct}%`}}/>
                                </div>
                                <div className="flex justify-between mt-1.5 text-[11px] font-mono text-white/60 tabular-nums">
                                    <span>{formatTime(elapsed)}</span>
                                    <span title="Time until next song">-{formatTime(remaining)}</span>
                                    <span>{formatTime(totalLength)}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <button onClick={isMuted ? handleTuneIn : handleTuneOut}
                                        aria-label={isMuted ? "Tune in (unmute)" : "Tune out (mute)"}
                                        className="flex-shrink-0 w-12 h-12 rounded-full bg-red-500 hover:bg-red-400 transition flex items-center justify-center shadow-lg shadow-red-500/30">
                                    {isMuted ? (
                                        <SpeakerXMarkIcon className="text-white w-6 h-6" aria-hidden="true"/>
                                    ) : (
                                        <SpeakerWaveIcon className="text-white w-6 h-6" aria-hidden="true"/>
                                    )}
                                </button>

                                <div className="flex-1 flex items-center gap-2">
                                    <input
                                        type="range"
                                        name="volume"
                                        id="volume"
                                        title="Volume"
                                        aria-label="Volume"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={Math.round(volume * 100)}
                                        className="flex-1 accent-red-400 h-1"
                                        onChange={(e) => setVolume(e.target.value / 100)}
                                    />
                                </div>

                                <button onClick={() => reportSong(s)}
                                        title="Report song"
                                        aria-label="Report song"
                                        className="flex-shrink-0 p-2 rounded-full hover:bg-white/10 transition">
                                    <FlagIcon className="fill-white/70 w-4 h-4" aria-hidden="true"/>
                                </button>
                            </div>
                        </div>
                    </div>

                    {upcoming.length > 0 && (
                        <div className="lg:col-span-1 space-y-3">
                            {upcoming.map((song, idx) => (
                                <li key={`${song.youtube_url}-${song.start_time}-${idx}`}>
                                    <Song song={{...song, category_name: idx === 0 ? 'Up next' : ''}} small
                                          compact={compactUpcoming}
                                          className="pointer-events-none"/>
                                </li>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div ref={containerRef} aria-hidden="true" style={{position: 'absolute', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none'}}/>
        </>
    );
}
